import { supabase } from './supabase'
import { viewingAs, viewAsTourSeen, rememberViewAsTourSeen } from './viewAsState'

/**
 * SHL-34 (David, 10 Oct): a click-through tour of the hub. Once, after the
 * welcome, the first time somebody opens the hub; remembered ON THE ACCOUNT
 * (158), so a new phone does not repeat it; and again from "Take the tour" in
 * the menu under the initials.
 *
 * ── IT POINTS, WHICH THE WELCOME DELIBERATELY DOES NOT ──────────────────────
 *
 * On 18 Sep David chose section CTAs over coach-marks for the WELCOME, because
 * a coach-mark is coupled to the DOM and the hub has two layouts (the rail and
 * the bubble above 820px, the tab bar and the topbar Ask One below it). That
 * argument is still why the welcome is cards. The tour is the thing that does
 * point, and it answers the objection rather than ignoring it:
 *
 *   1. A step names CANDIDATES, not an element. It points at the first one
 *      that is actually on screen — the tab bar's Star One on a phone, the
 *      rail's on a desktop — so one step serves both layouts (rule 11: one
 *      picture per destination).
 *   2. A step whose candidates are all absent (no contest running, One
 *      Question outside its weeks, a layout this file never heard of) is drawn
 *      as a centred card with no spotlight. It never breaks, and it never
 *      points at nothing.
 *
 * The suite checks that every selector below still exists in the source, so a
 * renamed class fails the build instead of silently turning a stop into a
 * centred card.
 */

export type TourStepKey = 'feed' | 'oneq' | 'askone' | 'stars' | 'photos' | 'menu'

export type TourStep = {
  key: TourStepKey
  /** Tried in order; the first VISIBLE match is the one pointed at. */
  targets: readonly string[]
}

/** The order is the order the eye meets them on the hub: the main column,
 *  then the tools around it, then the menu that brings you back here. */
export const TOUR_STEPS: readonly TourStep[] = [
  { key: 'feed', targets: ['.postfab__btn'] },
  { key: 'oneq', targets: ['.oneq'] },
  // The bubble above 820px, the topbar button below it — hub.css shows one.
  { key: 'askone', targets: ['.askbubble', '.askbar'] },
  // The tab on a phone, the rail item on a desktop: both carry data-tour.
  { key: 'stars', targets: ['[data-tour="stars"]'] },
  // The hub's contest card at every width when a contest is running; the
  // rail's Photos item on a desktop when one is not.
  { key: 'photos', targets: ['.contestcard', '[data-tour="photos"]'] },
  { key: 'menu', targets: ['.identitybtn'] },
]

/**
 * Nav routes that are tour stops, for the rail (AppShell) and the tab bar
 * (TabBar) to mark with `data-tour`. Keyed by route because the route is the
 * destination's identity; the label is translated, the route is not.
 */
export const TOUR_NAV: Readonly<Record<string, TourStepKey>> = {
  stars: 'stars',
  contests: 'photos',
}

/** Is this element drawn? display:none anywhere above it gives a zero box. */
export function isOnScreen(el: Element): boolean {
  const r = el.getBoundingClientRect()
  if (r.width <= 0 || r.height <= 0) return false
  const v = typeof getComputedStyle === 'function' ? getComputedStyle(el).visibility : 'visible'
  return v !== 'hidden' && v !== 'collapse'
}

/** The first candidate that is on screen, or null — which draws the step centred. */
export function findTarget(step: TourStep, root: ParentNode | null): Element | null {
  if (!root) return null
  for (const sel of step.targets) {
    for (const el of Array.from(root.querySelectorAll(sel))) {
      if (isOnScreen(el)) return el
    }
  }
  return null
}

export type Box = { top: number; left: number; width: number; height: number }

/**
 * Where the step's card goes. Below the target when it fits, above when it
 * does not, centred when neither does or there is no target. Always inside
 * the viewport by `margin`. Pure, so the suite can check it without a browser.
 */
export function placeCard(
  target: Box | null,
  view: { width: number; height: number },
  card: { width: number; height: number },
  gap = 14,
  margin = 12,
): { top: number; left: number; side: 'below' | 'above' | 'centre' } {
  const centre = {
    top: Math.max(margin, (view.height - card.height) / 2),
    left: Math.max(margin, (view.width - card.width) / 2),
    side: 'centre' as const,
  }
  if (!target) return centre
  const left = Math.min(
    Math.max(margin, target.left + target.width / 2 - card.width / 2),
    Math.max(margin, view.width - card.width - margin),
  )
  const below = target.top + target.height + gap
  if (below + card.height <= view.height - margin) return { top: below, left, side: 'below' }
  const above = target.top - gap - card.height
  if (above >= margin) return { top: above, left, side: 'above' }
  return centre
}

/* ── The seen-state: 158's two functions, about the caller alone ─────────── */

/**
 * Remembered for the life of the page. AppShell is rendered by every ROUTE,
 * so it remounts on every navigation — without this, every page change would
 * ask the database a question whose answer only ever goes one way.
 */
let seenCache: boolean | undefined

/**
 * Has this person finished or skipped the tour? THROWS on failure, and the
 * caller treats that as "do not start it": a tour on every visit because a
 * read failed is worse than no tour (it fails CLOSED, like the video gate).
 */
export async function loadTourSeen(): Promise<boolean> {
  if (seenCache !== undefined) return seenCache
  // 176. Finished while viewing as somebody: seen, for this session's preview.
  if (viewingAs() && viewAsTourSeen()) return (seenCache = true)
  const { data, error } = await supabase.rpc('my_tour_seen')
  if (error) throw error
  seenCache = data === true
  return seenCache
}

/** Finishing and skipping both record it. Optimistic and silent, like the
 *  welcome's dismissal: a failed write means the tour comes back next visit,
 *  which is the safe direction. */
export async function markTourSeen(): Promise<void> {
  seenCache = true
  // 176. Viewing as somebody: remembered here, never written for them.
  if (viewingAs()) {
    rememberViewAsTourSeen()
    return
  }
  const { error } = await supabase.rpc('mark_tour_seen')
  if (error) throw error
}

/**
 * "Take the tour" from the menu, on a page that is not the hub. The menu
 * navigates to the hub, which REMOUNTS the shell, so a state variable set
 * before the navigation would be gone after it. This one survives: the hub's
 * shell takes it on mount and starts the tour. Not a URL parameter, so a
 * reload or a shared link does not start a tour nobody asked for.
 */
let requested = false
export function requestTour(): void {
  requested = true
}
export function takeTourRequest(): boolean {
  const was = requested
  requested = false
  return was
}

/** Test-only: forget the cached answer. */
export function resetTourStateForTest(): void {
  seenCache = undefined
  requested = false
}
